import { useTranslation } from 'react-i18next';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SSHKey } from '../utils/types';
import SSHMobileKeyCardActions from './SSHMobileKeyCardActions';

interface SSHMobileKeyCardProps {
  sshKeys?: SSHKey[];
  reAddingKeyId?: number | null;
  onView: (id: number) => void;
  onDelete: (id: number) => void;
  onReAdd?: (id: number) => void;
  onRemove?: (id: number) => void;
  showStatusColumn?: boolean;
  showTypeColumn?: boolean;
  isServerOnline?: boolean;
}

const SSHMobileKeyCard = ({
  sshKeys = [],
  reAddingKeyId = null,
  onView,
  onDelete,
  onReAdd = undefined,
  onRemove = undefined,
  showStatusColumn = false,
  showTypeColumn = true,
  isServerOnline = true,
}: SSHMobileKeyCardProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'sshSettings',
  });

  return (
    <Stack spacing={2}>
      {sshKeys.map((sshKey) => {
        const isMissingFromServer = sshKey.status === 'notOnServer';

        return (
          <Card
            key={sshKey.id}
            sx={{
              bgcolor: isMissingFromServer ? 'warning.50' : 'inherit',
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                {/* Name */}
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('table.keyName')}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {sshKey.name}
                  </Typography>
                </Stack>

                {showTypeColumn && (
                  <Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('table.type')}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    >
                      {sshKey.alg_type}
                    </Typography>
                  </Stack>
                )}

                {/* Status */}
                {showStatusColumn && (
                  <Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t('table.status')}
                    </Typography>

                    <Typography
                      variant="body2"
                      color={
                        isMissingFromServer ? 'warning.dark' : 'success.dark'
                      }
                    >
                      {isMissingFromServer
                        ? t('status.notOnServer')
                        : t('status.active')}
                    </Typography>
                  </Stack>
                )}

                {/* Added */}
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('table.added')}
                  </Typography>

                  <Typography variant="body1">{sshKey.addedAt}</Typography>
                </Stack>

                {/* Missing key actions */}
                {isMissingFromServer ? (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('table.actions')}
                    </Typography>

                    <SSHMobileKeyCardActions
                      sshKey={sshKey}
                      reAddingKeyId={reAddingKeyId}
                      onView={onView}
                      onDelete={onDelete}
                      onReAdd={onReAdd}
                      onRemove={onRemove}
                      isServerOnline={isServerOnline}
                      mobileMissingActions
                    />
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      '& .MuiButton-root': {
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      },
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                      onClick={() => onView(sshKey.id)}
                    >
                      {t('actions.view')}
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      size="medium"
                      startIcon={<DeleteOutlineIcon fontSize="small" />}
                      onClick={() => onDelete(sshKey.id)}
                      disabled={!isServerOnline}
                    >
                      {t('actions.delete')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};
export default SSHMobileKeyCard;
